import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'mosaic_session_token';

// v6.36.25 — MODULE-LOAD auth setup (NOT inside React effect).
// Previously the auth interceptor was registered in an AuthProvider
// useEffect, which meant it was briefly absent during AuthProvider
// remount (e.g., after a demo quick-switch + window.location.href
// hard reload). Any in-flight POST/PUT/DELETE during that window
// went out without the Authorization header and the cookie-priority
// backend (services/auth.py reads cookie BEFORE the Authorization
// header — v6.36.14 commentary) read a stale or missing cookie and
// 401'd. The Translate-for-me bug surfaced this race; we hotfixed
// that single endpoint in v6.36.24 by passing the header inline.
//
// v6.36.25 hardens this for the entire app:
//   1. Register the interceptor ONCE at module load, before React
//      mounts. The interceptor reads from localStorage on EVERY
//      request, so token rotations are picked up automatically.
//   2. Set axios.defaults.withCredentials = true globally so the
//      session_token cookie is sent on every cross-origin request
//      alongside the Authorization header. Both auth signals reach
//      the backend every time; the cookie-priority logic still wins
//      when valid, and the Bearer header is the deterministic
//      fallback otherwise.
axios.defaults.withCredentials = true;
axios.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers = config.headers || {};
      // Don't overwrite an explicit Authorization header (e.g. the
      // v6.36.24 inline-pass-through in StudentFeedback.js translate
      // handler) so that defense-in-depth keeps working too.
      if (!config.headers['Authorization'] && !config.headers['authorization']) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }
  } catch (_e) { /* swallow — never block a request because of this */ }
  return config;
});

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // v6.36.12 — Synchronously hydrate from `mosaic_user_preload` (set by
  // the demo quick-switch handler in UserMenu.js right after it verifies
  // the new session). This eliminates the brief `user=null, loading=true`
  // window during the initial render that ProtectedRoute (allowedRoles=
  // ['student']) was bouncing on for the demo-student switch flow.
  // The preload is short-lived (~30 seconds) so a stale value can't
  // outlive a single navigation.
  const _readPreload = () => {
    try {
      const raw = localStorage.getItem('mosaic_user_preload');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Discard if older than 30 seconds.
      if (!parsed?.minted_at || Date.now() - parsed.minted_at > 30_000) {
        localStorage.removeItem('mosaic_user_preload');
        return null;
      }
      // One-shot: consume on read so subsequent mounts don't reuse it.
      localStorage.removeItem('mosaic_user_preload');
      return parsed.user || null;
    } catch (_e) {
      return null;
    }
  };
  const _preloadedUser = _readPreload();

  const [user, setUser] = useState(_preloadedUser);
  const [loading, setLoading] = useState(!_preloadedUser);
  const [isAuthenticated, setIsAuthenticated] = useState(!!_preloadedUser);

  // v6.36.25 — Axios interceptor is registered at MODULE LOAD above
  // (not in a useEffect) so it stays attached across AuthProvider
  // remounts and never has a "no interceptor" race window.

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // v6.32.219 — Token-tab guard. Detects when localStorage.mosaic_session_token
  // changes in a DIFFERENT browser tab (e.g., operator signs in as a different
  // role in tab B while tab A is still showing super_admin UI). Without this,
  // axios silently attaches the new token to subsequent requests from tab A —
  // surfacing as confusing 403s like "Super admin access required" mid-flow.
  //
  // Mechanic: the `storage` event fires ONLY in other tabs of the same origin
  // when a key changes. We compare the new value against the current cached
  // `user`'s session — if the token vanished or now belongs to a different
  // user_id, we drop the cached state and force a one-click re-check so the
  // UI reflects whatever the browser actually has stored.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== TOKEN_KEY) return;
      const newToken = e.newValue;
      // Token cleared in another tab → log out here too.
      if (!newToken) {
        if (user) {
          toast.warning('Signed out in another tab — refreshing.');
          setUser(null);
          setIsAuthenticated(false);
        }
        return;
      }
      // Token changed — we can't know whose token it is without a /me hit.
      // Re-run checkAuth so the UI matches the new token. Surface a toast so
      // the operator knows why the UI suddenly changed roles.
      toast.warning('Session changed in another tab — reloading your profile.');
      checkAuth();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [user, checkAuth]);

  const login = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/session`, 
        { session_id: sessionId },
      );
      if (response.data.session_token) {
        localStorage.setItem(TOKEN_KEY, response.data.session_token);
      }
      setUser(response.data.user);
      setIsAuthenticated(true);
      // v6.36.16 — Trigger MOI mascot greeting on every student login.
      if (response.data.user?.role === 'student') {
        try { sessionStorage.setItem('mosaic_pending_greeting', '1'); } catch (_e) {}
      }
      return response.data.user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithMicrosoft = async () => {
    try {
      const res = await axios.get(`${API}/sso/azure/config`);
      if (res.data.configured) {
        const loginRes = await axios.get(`${API}/sso/azure/login`);
        window.location.href = loginRes.data.redirect_url;
      } else {
        toast.info('Microsoft Azure AD SSO is not yet configured. Please contact your administrator or use email/password login.');
      }
    } catch {
      toast.info('Microsoft Azure AD SSO is not yet configured. Please contact your administrator or use email/password login.');
    }
  };

  const loginWithEmail = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, 
        { email, password },
      );
      if (response.data.session_token) {
        localStorage.setItem(TOKEN_KEY, response.data.session_token);
      }
      setUser(response.data.user);
      setIsAuthenticated(true);
      // v6.36.16 — Trigger MOI mascot greeting on every student login.
      if (response.data.user?.role === 'student') {
        try { sessionStorage.setItem('mosaic_pending_greeting', '1'); } catch (_e) {}
      }
      return response.data.user;
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail 
        : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join(' ')
        : 'Login failed. Please try again.';
      throw new Error(message);
    }
  };

  const loginWithClever = async () => {
    try {
      const res = await axios.get(`${API}/sso/clever/config`);
      if (res.data.configured) {
        const loginRes = await axios.get(`${API}/sso/clever/login`);
        window.location.href = loginRes.data.redirect_url;
      } else {
        toast.info('Clever SSO is not yet configured. Please contact your administrator or use email/password login.');
      }
    } catch {
      toast.info('Clever SSO is not yet configured. Please contact your administrator or use email/password login.');
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    loginWithGoogle,
    loginWithEmail,
    loginWithMicrosoft,
    loginWithClever,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
